import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { formatDubai } from '../../../utils/dubaiTime';
import ApartmentSetupClient from './ClientPropertyDetailsComponents/ApartmentSetup';
import ClientPropertyTasks from './ClientPropertyDetailsComponents/ClientPropertyTasks';
import BookingsList from './ClientPropertyDetailsComponents/BookingsList';
import RequestPropertyEditModal from '../ClientRequests/RequestPropertyEditModal';
import useClientRequestsEnabled from '../ClientRequests/useClientRequestsEnabled';
import axios from 'axios';
import './clientPropertyDetails.css';

function ClientPropertyDetails() {
  const { propertyId } = useParams();
  const [property, setProperty] = useState(null);

  // This property's linen deposits (one-time, refundable) — amount + the
  // bookings where they were charged.
  const [linenDeposits, setLinenDeposits] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get('/api/client-requests/wallet/linen-deposits')
      .then((res) => {
        if (cancelled) return;
        const all = res.data?.deposits || [];
        setLinenDeposits(all.filter((d) => Number(d.property_id) === Number(propertyId)));
      })
      .catch(() => { if (!cancelled) setLinenDeposits([]); });
    return () => { cancelled = true; };
  }, [propertyId]);

  const linenTotal = linenDeposits.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const linenRefunded = linenDeposits.reduce((s, d) => s + (parseFloat(d.refunded_total) || 0), 0);
  // Asia/Dubai render (item 8) — same "19 Jul 2026" visual as before.
  const formatDepositDate = (d) => {
    if (!d) return '—';
    return formatDubai(d, 'dd MMM yyyy') || String(d);
  };

  const [mapsCopied, setMapsCopied] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null); // full pending edit_property request row
  const [withdrawing, setWithdrawing] = useState(false);
  const requestsEnabled = useClientRequestsEnabled();

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`/api/client-requests/properties/${propertyId}`);
        setProperty(response.data);
      } catch (error) {
        console.error('Error fetching property:', error);
      }
    };

    fetchProperty();
  }, [propertyId]);

  // Does this property already have a pending change request?
  const fetchPendingEdit = useCallback(async () => {
    if (!requestsEnabled) { setPendingEdit(null); return; }
    try {
      const res = await axios.get('/api/client-requests/requests/my', {
        params: { status: 'pending', type: 'edit_property' },
      });
      const rows = Array.isArray(res.data?.requests) ? res.data.requests : [];
      setPendingEdit(rows.find((r) => String(r.target_id) === String(propertyId)) || null);
    } catch (err) {
      if (err.response?.status !== 503) console.error('Error checking pending edits:', err);
      setPendingEdit(null);
    }
  }, [propertyId, requestsEnabled]);

  useEffect(() => {
    fetchPendingEdit();
  }, [fetchPendingEdit]);

  const withdrawPendingEdit = async () => {
    if (!pendingEdit || withdrawing) return;
    if (!window.confirm('Withdraw this change request?')) return;
    setWithdrawing(true);
    try {
      await axios.post(`/api/client-requests/requests/${pendingEdit.id}/cancel`);
      await fetchPendingEdit();
    } catch (err) {
      console.error('Error withdrawing request:', err);
      // e.g. staff approved it moments ago — say so, then refresh to reality
      window.alert(err.response?.data?.error || 'Could not withdraw this request. Please refresh and try again.');
      await fetchPendingEdit();
    } finally {
      setWithdrawing(false);
    }
  };

  const formatDate = (dateString) => {
    const dubaiTime = DateTime.fromISO(dateString, { zone: 'Asia/Dubai' });
    return dubaiTime.toFormat('dd LLL yyyy - HH:mm');
  };

  const propertyIdDisplay = useMemo(() => {
    return `#${String(propertyId).padStart(5, '0')}`;
  }, [propertyId]);

  const handleCopyMaps = async () => {
    const raw = String(property?.google_maps_link || '').trim();
    if (!raw) return;

    try {
      await navigator.clipboard.writeText(raw);
    } catch (e) {
      const el = document.createElement('textarea');
      el.value = raw;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }

    setMapsCopied(true);
    window.setTimeout(() => setMapsCopied(false), 1200);
  };

  if (!property) {
    return (
      <div className="app-loading">
        <div className="app-spinner" />
        <div className="app-spinner-text">Loading Property Details...</div>
      </div>
    );
  }

  const propertyDisplay = property.unit_number ? `Unit ${property.unit_number} · ${property.building_name}` : property.building_name;

  return (
    <div className="cpd">
      <div className="cpd__header">
        <div className="cpd__headerSide cpd__headerSide--left">
          <span className={`cpd__statusBadge ${property.is_active === false ? 'cpd__statusBadge--archived' : 'cpd__statusBadge--active'}`}>
            {property.is_active === false ? 'Archived Property' : 'Active Property'}
          </span>
        </div>
        <div className="cpd__headerInner">
          <h1 className="cpd__title">Property ID: {propertyIdDisplay}</h1>
          <h2 className="cpd__subTitle">{propertyDisplay}</h2>
        </div>
        <div className="cpd__headerSide cpd__headerSide--right">
          {requestsEnabled && !pendingEdit && property.is_active !== false && (
            <button
              type="button"
              className="client-request-property-btn cpd__editBtn"
              onClick={() => setIsEditModalOpen(true)}
            >
              Edit Property
            </button>
          )}
        </div>
      </div>

      {/* Pending change panel — spells out exactly WHAT was requested */}
      {pendingEdit && (() => {
        const p = pendingEdit.payload || {};
        const changed = Array.isArray(p.changed_fields) ? p.changed_fields : [];
        const LABELS = {
          address_line_1: 'Building',
          unit_number: 'Unit',
          area: 'Area',
          bedrooms: 'Bedrooms',
          bathrooms: 'Bathrooms',
          single_beds: 'Single beds',
          double_beds: 'Queen / King Size beds',
          super_king_beds: 'Super King Size beds',
          required_cleaners: 'Cleaners',
          is_beach_towel: 'Beach towels',
          is_dolce_gusto: 'Dolce Gusto',
          is_nespresso: 'Nespresso',
          property_access: 'Access notes',
          wifi_name: 'WiFi name',
          wifi_password: 'WiFi password',
          notes: 'Notes',
          location: 'Map location',
          setup_photos: 'Setup photos',
        };
        const fmt = (field) => {
          if (field === 'location') return p.location_label || 'Updated pin';
          if (field === 'setup_photos') {
            const added = Array.isArray(p.setup_photos) ? p.setup_photos.length : 0;
            const removed = Array.isArray(p.setup_photo_removals) ? p.setup_photo_removals.length : 0;
            const noted = Array.isArray(p.setup_photo_updates) ? p.setup_photo_updates.length : 0;
            const parts = [];
            if (added) parts.push(`${added} added`);
            if (removed) parts.push(`${removed} removed`);
            if (noted) parts.push(`${noted} note${noted > 1 ? 's' : ''} updated`);
            return parts.join(' · ') || 'Updated';
          }
          const v = p[field];
          if (field === 'bedrooms') return v === 0 ? 'Studio' : String(v ?? '—');
          if (typeof v === 'boolean') return v ? 'Yes' : 'No';
          if (v == null || String(v).trim() === '') return '(cleared)';
          return String(v);
        };
        return (
          <div className="cpd__pendingPanel">
            <div className="cpd__pendingPanelHead">
              <span className="cpd__pendingEditChip">Change requested</span>
              <span className="cpd__pendingPanelSub">Awaiting our team&apos;s review</span>
              <button
                type="button"
                className="cpd__pendingWithdraw"
                onClick={withdrawPendingEdit}
                disabled={withdrawing}
              >
                {withdrawing ? 'Withdrawing…' : 'Withdraw request'}
              </button>
            </div>
            <div className="cpd__pendingRows">
              {changed.map((field) => (
                <div className="cpd__pendingRow" key={field}>
                  <span>{LABELS[field] || field}</span>
                  <strong>{fmt(field)}</strong>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {isEditModalOpen && (
        <RequestPropertyEditModal
          property={property}
          onClose={() => setIsEditModalOpen(false)}
          onSubmitted={fetchPendingEdit}
        />
      )}

      <div className="cpd__container">
        {/* ✅ Top row: General + Address (same row on desktop) */}
        <div className="cpd__topRow">
          <div className="cpdCard">
            <div className="cpdCard__head">
              <h2 className="cpdCard__title">General Details</h2>
            </div>

            <div className="cpdGrid">
              <div className="cpdField">
                <div className="cpdField__label">Last Booking Date</div>
                <div className="cpdField__value">
                  {property.last_booking_date ? (
                    <Link className="cpdLink" to={`/client/bookings/${property.last_booking_id}`}>
                      {formatDate(property.last_booking_date)}
                    </Link>
                  ) : (
                    <span className="cpdMuted">No past bookings yet</span>
                  )}
                </div>
              </div>

              <div className="cpdField">
                <div className="cpdField__label">Next Booking Date</div>
                <div className="cpdField__value">
                  {property.next_booking_date ? (
                    <Link className="cpdLink" to={`/client/bookings/${property.next_booking_id}`}>
                      {formatDate(property.next_booking_date)}
                    </Link>
                  ) : (
                    <span className="cpdMuted">No future bookings yet</span>
                  )}
                </div>
              </div>

              <div className="cpdRow">
                <div className="cpdField">
                  <div className="cpdField__label">Bedrooms</div>
                  <div className="cpdField__value">{property.bedrooms === 0 ? 'Studio' : property.bedrooms}</div>
                </div>
                <div className="cpdField">
                  <div className="cpdField__label">Bathrooms</div>
                  <div className="cpdField__value">{property.bathrooms ?? '—'}</div>
                </div>
              </div>

              <div className="cpdRow">
                <div className="cpdField">
                  <div className="cpdField__label">Queen / King Size Beds</div>
                  <div className="cpdField__value">{property.double_beds}</div>
                </div>
                <div className="cpdField">
                  <div className="cpdField__label">Super King Size Beds</div>
                  <div className="cpdField__value">{property.super_king_beds || 0}</div>
                </div>
                <div className="cpdField">
                  <div className="cpdField__label">Single Beds</div>
                  <div className="cpdField__value">{property.single_beds}</div>
                </div>
              </div>

              <div className="cpdField">
                <div className="cpdField__label">Linen Deposit Amount</div>
                <div className="cpdField__value">
                  {linenDeposits.length === 0 ? (
                    <span className="cpdMuted">No deposit on record</span>
                  ) : (
                    <>AED {linenTotal.toFixed(2)}{linenRefunded > 0 ? <span className="cpdMuted"> (AED {linenRefunded.toFixed(2)} refunded)</span> : null}</>
                  )}
                </div>
              </div>

              <div className="cpdField">
                <div className="cpdField__label">Linen Deposit Dates</div>
                <div className="cpdField__value">
                  {linenDeposits.length === 0 ? (
                    <span className="cpdMuted">—</span>
                  ) : (
                    linenDeposits.map((d, i) => (
                      <React.Fragment key={d.id}>
                        {i > 0 && ', '}
                        {d.booking_id ? (
                          <Link className="cpdLink" to={`/client/bookings/${d.booking_id}`}>
                            {formatDepositDate(d.booking_date || d.charged_at)}
                          </Link>
                        ) : (
                          <span>{formatDepositDate(d.booking_date || d.charged_at)}</span>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="cpdCard">
            <div className="cpdCard__head">
              <h2 className="cpdCard__title">Address Information</h2>
            </div>

            <div className="cpdGrid">
              <div className="cpdField">
                <div className="cpdField__label">Building Name</div>
                <div className="cpdField__value">{property.building_name}</div>
              </div>

              <div className="cpdField">
                <div className="cpdField__label">Unit Number</div>
                <div className="cpdField__value">{property.unit_number}</div>
              </div>

              <div className="cpdField">
                <div className="cpdField__label">Area</div>
                <div className="cpdField__value">{property.area}</div>
              </div>

              {property.google_maps_link ? (
                <div className="cpdField cpdField--wide">
                  <div className="cpdField__label">Google Maps</div>

                  <div className="cpdMapsRow">
                    <a
                      className="cpdMapsLink"
                      href={property.google_maps_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={property.google_maps_link}
                    >
                      {property?.google_maps_link}
                    </a>

                    <button
                      type="button"
                      className={`cpdIconBtn ${mapsCopied ? 'is-copied' : ''}`}
                      onClick={handleCopyMaps}
                      aria-label={mapsCopied ? 'Copied' : 'Copy link'}
                      title={mapsCopied ? 'Copied' : 'Copy link'}
                    >
                      {mapsCopied ? (
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                          <path
                            d="M20 6L9 17l-5-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                          <path
                            d="M8 8h10v12H8V8z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6 16H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ✅ Cards rendered HERE so all cards look identical */}
        <div className="cpdCard">
          <BookingsList propertyId={propertyId} />
        </div>

        <div className="cpdCard">
          <ApartmentSetupClient propertyId={propertyId} />
        </div>

        <div className="cpdCard">
          <ClientPropertyTasks propertyId={propertyId} />
        </div>
      </div>
    </div>
  );
}

export default ClientPropertyDetails;
